import React, { useState, useEffect, useRef } from 'react';
import { Bell, Check, CheckCheck, Trash2, ExternalLink } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Badge } from "@/components/ui/badge";

const NotificationBell = () => {
  const { api, isAuthenticated, isFullyAuthenticated } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();

  const fetchUnread = async () => {
    try {
      const res = await api.get('/notifications/unread-count');
      setUnreadCount(res.data.count);
    } catch {}
  };

  const fetchNotifications = async () => {
    try {
      const res = await api.get('/notifications/?limit=30');
      setNotifications(res.data);
    } catch {}
  };

  useEffect(() => {
    if (!isFullyAuthenticated) return;
    fetchUnread();
    const interval = setInterval(fetchUnread, 15000);
    return () => clearInterval(interval);
  }, [isFullyAuthenticated]);

  useEffect(() => {
    if (open) fetchNotifications();
  }, [open]);

  useEffect(() => {
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const markRead = async (id) => {
    try {
      await api.put(`/notifications/${id}/read`);
      setNotifications(prev => prev.map(n => n._id === id ? { ...n, read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch {}
  };

  const markAllRead = async () => {
    try {
      await api.put('/notifications/read-all');
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch {}
  };

  const deleteNotif = async (id) => {
    try {
      await api.delete(`/notifications/${id}`);
      const removed = notifications.find(n => n._id === id);
      setNotifications(prev => prev.filter(n => n._id !== id));
      if (removed && !removed.read) setUnreadCount(prev => Math.max(0, prev - 1));
    } catch {}
  };

  const handleClick = (notif) => {
    if (!notif.read) markRead(notif._id);
    if (notif.link) {
      navigate(notif.link);
      setOpen(false);
    }
  };

  const typeColor = (type) => {
    if (type?.includes('approved')) return 'text-green-400';
    if (type?.includes('rejected')) return 'text-red-400';
    if (type?.includes('completed')) return 'text-blue-400';
    if (type?.includes('new_car')) return 'text-orange-400';
    return 'text-gray-300';
  };

  const typeIcon = (type) => {
    if (type?.includes('approved')) return '✅';
    if (type?.includes('rejected')) return '❌';
    if (type?.includes('completed')) return '🏁';
    if (type?.includes('request')) return '📋';
    if (type?.includes('new_car')) return '🚗';
    if (type?.includes('promo')) return '🎫';
    return '🔔';
  };

  const timeAgo = (dateStr) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'most';
    if (mins < 60) return `${mins} perce`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours} órája`;
    const days = Math.floor(hours / 24);
    return `${days} napja`;
  };

  if (!isFullyAuthenticated) return null;

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className="relative p-2 rounded-full hover:bg-white/10 transition-colors"
        data-testid="notification-bell"
      >
        <Bell className="h-5 w-5 text-gray-300" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 h-5 w-5 bg-orange-500 rounded-full flex items-center justify-center text-[10px] font-bold text-white animate-pulse" data-testid="notification-badge">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-80 sm:w-96 bg-black/95 border border-orange-500/20 rounded-xl shadow-2xl shadow-black/60 z-[100] overflow-hidden" data-testid="notification-dropdown">
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
            <span className="font-semibold text-sm text-white">Értesítések</span>
            {unreadCount > 0 && (
              <button onClick={markAllRead} className="text-xs text-orange-400 hover:text-orange-300 flex items-center gap-1" data-testid="mark-all-read-btn">
                <CheckCheck className="h-3.5 w-3.5" /> Mind olvasott
              </button>
            )}
          </div>

          <div className="max-h-[400px] overflow-y-auto custom-scrollbar">
            {notifications.length === 0 ? (
              <div className="py-10 text-center text-gray-500 text-sm">Nincs értesítés</div>
            ) : (
              notifications.map((n) => (
                <div
                  key={n._id}
                  className={`flex items-start gap-3 px-4 py-3 border-b border-white/5 hover:bg-white/5 transition-colors cursor-pointer ${!n.read ? 'bg-orange-500/5' : ''}`}
                  onClick={() => handleClick(n)}
                  data-testid={`notification-item-${n._id}`}
                >
                  <span className="text-lg mt-0.5 flex-shrink-0">{typeIcon(n.type)}</span>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium truncate ${!n.read ? 'text-white' : 'text-gray-400'}`}>{n.title}</p>
                    <p className="text-xs text-gray-500 truncate">{n.message}</p>
                    <p className="text-[10px] text-gray-600 mt-1">{timeAgo(n.created_at)}</p>
                  </div>
                  <div className="flex flex-col gap-1 flex-shrink-0">
                    {!n.read && (
                      <button onClick={(e) => { e.stopPropagation(); markRead(n._id); }} className="p-1 hover:bg-green-500/20 rounded" title="Olvasott">
                        <Check className="h-3.5 w-3.5 text-green-400" />
                      </button>
                    )}
                    <button onClick={(e) => { e.stopPropagation(); deleteNotif(n._id); }} className="p-1 hover:bg-red-500/20 rounded" title="Törlés">
                      <Trash2 className="h-3.5 w-3.5 text-red-400" />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
